import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  meditationPositionAt,
  meditationTotalMs,
  type CoachingPractice,
  type MeditationStep,
} from '@mlm/shared/coaching';
import { linkButton } from './panels/today';
import { CoachButton, Eyebrow, SageCheck } from './primitives';
import { toolHeading } from '../pages/tool-frame';
import { clockLabel } from '../lib/format';
import { coaching } from '../lib/api';
import { useRefreshCoaching } from '../lib/queries';

type Status = 'idle' | 'running' | 'paused' | 'done';

/**
 * A practice walked one step at a time on a timer: the guided meditations and
 * the manifestation toolkit are the same interaction, so they are the same
 * screen.
 *
 * The clock is read from the wall and the position derived from elapsed time
 * (`meditationPositionAt`), never counted in ticks — a throttled tab or a
 * locked phone must not leave the member on a step the session has passed.
 * The practice is recorded once, when the last step runs out.
 */
export function SteppedSession({
  eyebrow,
  title,
  intro,
  meta,
  steps,
  practice,
  event,
  backTo,
  disclaimer,
}: {
  eyebrow: ReactNode;
  title: string;
  intro: ReactNode;
  meta?: ReactNode;
  steps: MeditationStep[];
  practice: CoachingPractice;
  /** Fired once at the end, with whatever the caller wants recorded. */
  event: () => void;
  backTo: string;
  disclaimer: string;
}) {
  const refresh = useRefreshCoaching();
  const [status, setStatus] = useState<Status>('idle');
  /** Milliseconds banked by earlier runs; the current run adds `now - startedAt`. */
  const [banked, setBanked] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const recorded = useRef(false);

  const complete = useMutation({
    mutationFn: () => coaching.practice(practice),
    meta: { silentErrorCodes: ['coaching_pro_required'] },
    onSuccess: () => void refresh(),
  });

  useEffect(() => {
    if (status !== 'running' || startedAt === null) return;
    const tick = () => setElapsed(banked + (Date.now() - startedAt));
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [status, startedAt, banked]);

  const position = meditationPositionAt(steps, elapsed);
  const totalMs = meditationTotalMs(steps);

  useEffect(() => {
    if (status !== 'running' || !position.done) return;
    setStatus('done');
  }, [status, position.done]);

  useEffect(() => {
    if (status !== 'done' || recorded.current) return;
    recorded.current = true;
    event();
    complete.mutate();
  }, [status, event, complete]);

  // Keep the screen on while a session runs; a phone that dims loses the member.
  const running = status === 'running';
  useEffect(() => {
    if (!running || !('wakeLock' in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let released = false;
    navigator.wakeLock
      .request('screen')
      .then((l) => {
        if (released) void l.release();
        else lock = l;
      })
      .catch(() => undefined);
    return () => {
      released = true;
      void lock?.release();
    };
  }, [running]);

  const start = () => {
    setStartedAt(Date.now());
    setStatus('running');
  };
  const pause = () => {
    if (startedAt !== null) setBanked((b) => b + (Date.now() - startedAt));
    setStartedAt(null);
    setStatus('paused');
  };
  const resume = () => {
    setStartedAt(Date.now());
    setStatus('running');
  };

  const step = steps[position.index]!;
  const remaining = Math.max(0, Math.ceil((totalMs - position.elapsedMs) / 1000));
  const stepRemaining = Math.max(
    0,
    Math.ceil((step.seconds * 1000 - position.stepElapsedMs) / 1000),
  );

  return (
    <>
      <Eyebrow className="mb-3">{eyebrow}</Eyebrow>
      <h1 className={toolHeading}>{title}</h1>
      <div className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">{intro}</div>
      {meta && <p className="eyebrow mt-3">{meta}</p>}

      {status === 'idle' ? (
        <ol className="mt-10 space-y-4">
          {steps.map((s, i) => (
            <li key={i} className="flex gap-3.5">
              <span className="eyebrow w-6 shrink-0 tabular-nums">{i + 1}</span>
              <span className="flex-1 text-[0.95rem] leading-relaxed text-ink/70">{s.text}</span>
            </li>
          ))}
        </ol>
      ) : (
        <section className="mt-10" aria-live="polite">
          <Eyebrow>
            Step {position.index + 1} of {steps.length}
            {status === 'paused' && ' · Paused'}
          </Eyebrow>
          <p className="mt-4 font-display text-[1.5rem] leading-snug text-ink lg:text-[1.875rem]">
            {step.text}
          </p>
          {status !== 'done' && (
            <p className="eyebrow mt-5 tabular-nums">
              {clockLabel(stepRemaining)} on this step · {clockLabel(remaining)} left
            </p>
          )}
          <div className="mt-6 h-px w-full bg-[rgba(16,32,26,0.14)]">
            <div
              className="h-px bg-verde transition-[width] duration-300"
              style={{ width: `${totalMs === 0 ? 100 : (position.elapsedMs / totalMs) * 100}%` }}
            />
          </div>
        </section>
      )}

      {status === 'done' && (
        <section className="mt-10 border-t border-border pt-6">
          <p className="flex items-center gap-2 text-[0.95rem] text-ink">
            <SageCheck size={20} /> Session complete
          </p>
          {complete.data && (
            <p className="eyebrow mt-3">
              Today's Success Score: {complete.data.score.total} · {complete.data.score.label}
            </p>
          )}
        </section>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-4 pt-10">
        {status === 'idle' && (
          <CoachButton kind="gold" block className="lg:w-auto lg:px-11" onClick={start}>
            Begin
          </CoachButton>
        )}
        {status === 'running' && (
          <CoachButton block className="lg:w-auto lg:px-11" onClick={pause}>
            Pause
          </CoachButton>
        )}
        {status === 'paused' && (
          <>
            <CoachButton kind="gold" className="flex-1 lg:flex-none lg:px-11" onClick={resume}>
              Resume
            </CoachButton>
            <Link to={backTo} className="eyebrow underline underline-offset-4">
              End session
            </Link>
          </>
        )}
        {status === 'done' && (
          <Link to={backTo} className={linkButton}>
            Choose another
          </Link>
        )}
      </div>
      <p className="pt-8 text-xs leading-relaxed text-muted-foreground">{disclaimer}</p>
    </>
  );
}
